import React, {useCallback, useEffect, useState} from 'react';
import '@telegram-apps/telegram-ui/dist/styles.css';
import './App.css';
import {
    Accordion,
    AppRoot,
    Avatar,
    Badge,
    Button,
    Cell,
    Divider,
    Modal,
    Placeholder,
    Section,
    Snackbar,
    Spinner,
    Switch,
    TabsList,
    Text,
    Textarea,
    Title
} from "@telegram-apps/telegram-ui/dist/components";
import {TabsItem} from "@telegram-apps/telegram-ui/dist/components/Navigation/TabsList/components/TabsItem/TabsItem";
import {
    ModalHeader
} from "@telegram-apps/telegram-ui/dist/components/Overlays/Modal/components/ModalHeader/ModalHeader";
import {Icon16Chevron} from "@telegram-apps/telegram-ui/dist/icons/16/chevron";
import WebApp from "@twa-dev/sdk";
import {WebAppUser} from "@twa-dev/types";
import {
    Answer,
    cancelProfileAnswer,
    deleteProfileQuestion,
    getAuthProfile,
    getProfile,
    Profile,
    Question,
    saveProfilePhoto, sendBotImage,
    sendProfileAnswer,
    sendProfileQuestion
} from "./api/api";
import {resolveBackgroundColor} from "./scripts/resolve-tg-color";
import {PopupButton} from "@tma.js/sdk-react";
import {useTranslation} from "react-i18next";
import CompressedImageInput from "./components/CompressedImageInput";
import {BackButton} from "@twa-dev/sdk/react";
import ReactGA from "react-ga4";
import html2canvas from "html2canvas";
import {
    AccordionSummary
} from "@telegram-apps/telegram-ui/dist/components/Blocks/Accordion/components/AccordionSummary/AccordionSummary";
import {
    AccordionContent
} from "@telegram-apps/telegram-ui/dist/components/Blocks/Accordion/components/AccordionContent/AccordionContent";
import {useSwipeable} from "react-swipeable";

const App = () => {
    let startProfileId: number;
    if (WebApp.initDataUnsafe.start_param) {
        startProfileId = parseInt(WebApp.initDataUnsafe.start_param);
    } else {
        startProfileId = WebApp.initDataUnsafe.user?.id ?? 0;
    }

    let initAllowActions: boolean = false;
    if (WebApp.initDataUnsafe.user) {
        initAllowActions = WebApp.initDataUnsafe.user.allows_write_to_pm ?? false;
        initAllowActions = initAllowActions || (WebApp.platform !== "ios" && WebApp.platform === "android");
    }

    const { t, i18n: {language} } = useTranslation();
    const [history, setHistory] = useState<number[]>([startProfileId]);
    const [allowActions, setAllowActions] = useState<boolean>(initAllowActions);
    const [authProfile, setAuthProfile] = useState<WebAppUser|null>(null);
    const [authProfileFetching, setAuthProfileFetching] = useState(true);
    const [profileId, setProfileId] = useState<number>(startProfileId);
    const [profile, setProfile] = useState<Profile|null>(null);
    const [profileFetching, setProfileFetching] = useState(true);
    const [bottomMargin, setBottomMargin] = useState<string>("mt-32");

    const [error, setError] = useState<string|null>(null);

    const goBack = () => {
        if (history.length > 1) {
            let newHistory = [...history];
            newHistory.pop();
            setHistory(newHistory);
            setProfileId(newHistory[newHistory.length - 1]);
        }
    };

    const fetchProfile = useCallback((profileId: number) => {
        if (error === "profile_fetching_error") {
            setError(null);
        }
        setProfileFetching(true);
        getProfile(profileId).then((data) => {
            setProfile(data);
            setProfileFetching(false);
        }).catch((error) => {
            if (!error.response || error.response.status !== 404) {
                setError("profile_fetching_error")
                setProfileFetching(false);
            }
            setProfileId(WebApp.initDataUnsafe.user?.id ?? 0);
        })
    }, [error])

    const fetchAuthProfile = useCallback(async (profileId: number) => {
        setAuthProfileFetching(true);
        getAuthProfile().then((data) => {
            if (data.registered_now) {
                ReactGA.event("registered", {auth_user_id: data.id, start_param: WebApp.initDataUnsafe.start_param ?? "bot"});
            }
            setAuthProfile(data);
            setAuthProfileFetching(false);
            fetchProfile(profileId);
            WebApp.HapticFeedback.impactOccurred("soft");
        }).catch((error) => {
            console.error("error fetching", error);
            setError("auth_profile_fetching_error")
            WebApp.HapticFeedback.notificationOccurred("error");
            setAuthProfileFetching(false);
        })
    }, [fetchProfile])

    useEffect( () => {
        ReactGA.event("open_profile", {auth_user_id: authProfile?.id, profile_id: profileId});
        resolveBackgroundColor();
        fetchAuthProfile(profileId).then();
    }, [fetchAuthProfile, profileId, authProfile?.id]);

    const [answerSettingsOpened, setAnswerSettingsOpened] = useState(false);
    const [activeTab, setActiveTab] = useState("answers");
    const [sendQuestionLoading, setSendQuestionLoading] = useState(false);
    const [sendAnswerLoading, setSendAnswerLoading] = useState(false);
    const [currentAnswerQuestionId, setCurrentAnswerQuestionId] = useState<number>(0);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [currentActionQuestion, setCurrentActionQuestion] = useState<Question | null>(null);
    const [snackbar, setSnackbar] = useState<React.ReactNode | null>(null);
    const [newProfilePhotoSrc, setNewProfilePhotoSrc] = useState<string>("");
    const [newProfilePhotoLoading, setNewProfilePhotoLoading] = useState<boolean>(false);
    const [newProfilePhotoName, setNewProfilePhotoName] = useState<string>(t("select_file"));
    const [profilePhotoModalOpened, setProfilePhotoModalOpened] = useState(false);
    const [askedQuestionsOpened, setAskedQuestionsOpened] = useState(false);

    const mustActionAllowed = (callback: Function) => {
        if (!allowActions) {
            const grantPmAccess = (allowed: boolean) => {
                if (allowed) {
                    callback();
                    setAllowActions(true);
                }
            };
            WebApp.HapticFeedback.impactOccurred("heavy");
            WebApp.requestWriteAccess(grantPmAccess);
            return;
        }
        callback();
    }

    const goToProfile = (profileId: number) => {
        window.scrollTo(0, 0);
        endAction();
        setProfileId(profileId);
        fetchProfile(profileId);
        let newHistory = [...history];
        newHistory.push(profileId);
        setHistory(newHistory);
    }

    const goToCurrentQuestionProfile = () => {
        if (currentActionQuestion?.from_user) {
            goToProfile(currentActionQuestion.from_user.id);
        }
    }

    const goToProfileByEvent = (e: React.MouseEvent<HTMLElement>) => {
        const profileId = parseInt(e.currentTarget.dataset.profileid ? e.currentTarget.dataset.profileid : "0");
        const toTab = e.currentTarget.dataset.totab;
        if (profileId) {
            goToProfile(profileId);
            if (toTab) {
                setActiveTab(toTab);
            }
        }
    }

    const goToMyProfile = () => {
        if (authProfile) {
            mustActionAllowed(() => {
                goToProfile(authProfile.id);
            })
        }
    }

    const getAcronym = (user: WebAppUser|null|undefined) => {
        if (!user) {
            return "N/A";
        }
        let acronym = user.first_name.charAt(0);
        if (user.last_name) {
            acronym += user.last_name.charAt(0);
        }
        return acronym;
    }

    const getProfileLink = (profileId: number) => {
        return "https://t.me/askerixbot/ask?startapp=" + profileId;
    }

    const shareProfile = () => {
        let link = "https://t.me/share/url?url=" + encodeURIComponent(getProfileLink(profileId)) + "&text=";
        let content: string;
        if (authProfile?.id === profileId) {
            content = t("share_profile_owner_text");
        } else {
            content = t("share_profile_text", {first_name: authProfile?.first_name, last_name: authProfile?.last_name});
        }
        link += encodeURIComponent(content);
        ReactGA.event('share');
        WebApp.openTelegramLink(link);
    }

    const showSnackbar = (emoji: string, messageKey: string) => {
        const message = t(messageKey);
        setSnackbar(<Snackbar before={emoji} className={"snackbar-fixed"} onClose={()=>{setSnackbar(null)}} duration={3000}>{message}</Snackbar>)
    }

    const showDialog = (messageKey:string, button:PopupButton, callback: Function, title?:string) => {
        const message = t(messageKey);
        const params = {
            title: title,
            message: message,
            buttons: [{type: "cancel" }, button],
        };
        WebApp.HapticFeedback.notificationOccurred("warning");
        // @ts-ignore
        WebApp.showPopup(params, callback);
    };

    const onActiveTabChange = (tab: string) => {
        setActiveTab(tab);
        WebApp.HapticFeedback.impactOccurred("light");
    }

    const formatDateString = (date: string) => {
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString(language, {year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "numeric"});
    }

    const endAction = () => {
        setAnswerSettingsOpened(false);
        setCurrentAnswerQuestionId(0);
        setCurrentActionQuestion(null);
        setSendAnswerLoading(false);
        setSendQuestionLoading(false);
        setDeleteLoading(false);
        setCancelLoading(false);
        setBottomMargin("mt-32");
        setProfilePhotoModalOpened(false);
        setShareAnswerInProgress(false);
    }

    const stateAnswerAction = (stateOpened: boolean) => {
        if (stateOpened) {
            setAnswerSettingsOpened(true);
        } else {
            endAction();
        }
    }

    const focusQuestionField = () => {
        let questionField = document.getElementById("questionField") as HTMLInputElement;
        questionField.focus();
    }
    const sendQuestion = () => {
        const callback = () => {
            let questionField = document.getElementById("questionField") as HTMLInputElement;
            let anonymouslySwitch = document.getElementById("switchAnonymously") as HTMLInputElement;
            if (questionField.value === "") {
                questionField.focus();
                return;
            }
            ReactGA.event("send_question", {
                auth_user_id: authProfile?.id,
                to_profile_id: profileId,
                anonymously: anonymouslySwitch.checked
            });
            setSendQuestionLoading(true);
            sendProfileQuestion(profileId, questionField.value, anonymouslySwitch.checked).then(() => {
                questionField.value = "";
                fetchProfile(profileId);
                WebApp.HapticFeedback.notificationOccurred("success");
                showSnackbar("✅", "question_sent")
                setSendQuestionLoading(false);
                ReactGA.event("question_sent", {auth_user_id: authProfile?.id})
            }).catch(() => {
                WebApp.HapticFeedback.notificationOccurred("error");
                showSnackbar("❌", "question_send_error")
                setSendQuestionLoading(false);
                ReactGA.event("question_send_error", {auth_user_id: authProfile?.id})
            })
        }
        mustActionAllowed(callback);
    }

    if (currentAnswerQuestionId && bottomMargin !== "mt-80") {
        setBottomMargin("mt-80");
    }

    const scrollToAnswerButton = () => {
        setTimeout(() => {
            const questionSection = document.getElementById(`question${currentAnswerQuestionId}`);
            if (questionSection) {
                questionSection.scrollIntoView({block: "start", behavior: "smooth"});
            }
        }, 500);
    }

    const captureAndShareElement = async (elementId: string): Promise<string> => {
        const element = document.getElementById(elementId) as HTMLElement;
        const bg = window.getComputedStyle(document.documentElement).getPropertyValue('--tg-theme-secondary-bg-color');
        return new Promise(async (resolve, reject) => {
            try {
                const canvas = await html2canvas(element, {
                    backgroundColor: bg, onclone: (_, element: HTMLElement) => {
                        element.classList.add("p-2");
                        const child = document.createElement("div");
                        child.innerHTML = t("answer_from_bot");
                        child.classList.add("text-center", "share-bot-name");
                        element.appendChild(child);
                    }
                })
                canvas.toBlob((blob) => {
                    if (blob) {
                        const file = new File([blob], "askerixbot.png", {type: "image/png"});
                        if (!!navigator.canShare && navigator.canShare({files: [file]})) {
                            navigator.share({files: [file]}).catch(() => {
                                reject(new Error("cancel_share"));
                            }).then(() => {
                                resolve("sent");
                            });
                        } else {
                            sendBotImage(file).then(() => {
                                resolve("sent_by_bot");
                            }).catch((error) => {
                                reject(error);
                            });
                        }
                    } else {
                        reject(new Error("blob_not_found"));
                    }
                });
            } catch (error) {
                reject(error);
            }
        });
    }

    const startAnswering = (e: React.MouseEvent<HTMLElement>) => {
        const questionId = parseInt(e.currentTarget.dataset.questionid ? e.currentTarget.dataset.questionid : "");
        if (!questionId) {
            console.error("question id not found", e.currentTarget);
            return;
        }
        setCurrentAnswerQuestionId(questionId);
        WebApp.HapticFeedback.impactOccurred("soft");
    }

    const sendAnswer = () => {
        let answerField = document.getElementById("answerField") as HTMLInputElement;
        if (answerField.value === "") {
            answerField.focus();
            return;
        }
        ReactGA.event("send_answer", {
            auth_user_id: authProfile?.id,
            to_profile_id: profileId,
        });
        setSendAnswerLoading(true)
        sendProfileAnswer(profileId, currentAnswerQuestionId, answerField.value).then(() => {
            answerField.value = "";
            fetchProfile(profileId);
            WebApp.HapticFeedback.notificationOccurred("success");
            showSnackbar("✅", "answer_sent")
            endAction()
            ReactGA.event("answer_sent", {auth_user_id: authProfile?.id})
        }).catch(() => {
            WebApp.HapticFeedback.notificationOccurred("error");
            showSnackbar("❌", "answer_send_error")
            endAction()
            ReactGA.event("answer_send_error", {auth_user_id: authProfile?.id})
        })
    }

    const deleteQuestion = (e: React.MouseEvent<HTMLElement>) => {
        const questionId = parseInt(e.currentTarget.dataset.questionid ? e.currentTarget.dataset.questionid : "");

        const doDelete = () => {
            setDeleteLoading(true);
            deleteProfileQuestion(profileId, questionId).then(() => {
                fetchProfile(profileId);
                WebApp.HapticFeedback.notificationOccurred("success");
                showSnackbar("✅", "question_deleted")
                endAction()
            }).catch(() => {
                WebApp.HapticFeedback.notificationOccurred("error");
                showSnackbar("❌", "question_delete_error")
                endAction()
            })
        }
        if (WebApp.platform === "unknown") {
            const confirmDelete = window.confirm("Вы уверены, что хотите удалить этот вопрос?");
            if (confirmDelete) {
                doDelete();
            }
            return;
        }

        const wrapForDialog = (id: string) => {
            if (id === "yes") {
                doDelete();
            }
        }

        showDialog("delete_question_confirmation", {id: "yes", type: "destructive", text:t("delete")}, wrapForDialog, t("delete_title"))
    }

    const [shareAnswerInProgress, setShareAnswerInProgress] = useState(false)

    const shareAnswer = async (e: React.MouseEvent<HTMLElement>) => {
        const questionId = parseInt(e.currentTarget.dataset.questionid ? e.currentTarget.dataset.questionid : "");
        if (!questionId) {
            console.error("question id not found", e.currentTarget);
            return;
        }
        const callback = async () => {
            setShareAnswerInProgress(true);
            ReactGA.event("start_share_answer", {auth_user_id: authProfile?.id, question_id: questionId});
            await captureAndShareElement("answer" + questionId).then((messageKey) => {
                endAction();
                showSnackbar("✅", messageKey)
            }).catch((error) => {
                setShareAnswerInProgress(false);
                if (error.message === "cancel_share") {
                    ReactGA.event("answer_share_canceled", {auth_user_id: authProfile?.id, question_id: questionId});
                    return;
                }
                showSnackbar("❌", "unknown_error")
                ReactGA.event("answer_share_error", {
                    auth_user_id: authProfile?.id,
                    question_id: questionId,
                    error: error.message
                });
            });
            ReactGA.event("answer_shared", {auth_user_id: authProfile?.id, question_id: questionId});
        }
        mustActionAllowed(callback);
    }

    const cancelAnswer = (e: React.MouseEvent<HTMLElement>) => {
        const questionId = parseInt(e.currentTarget.dataset.questionid ? e.currentTarget.dataset.questionid : "");
        const doCancel = () => {
            setCancelLoading(true);
            cancelProfileAnswer(profileId, questionId).then(() => {
                fetchProfile(profileId);
                WebApp.HapticFeedback.notificationOccurred("success");
                showSnackbar("✅", "answer_cancelled")
                endAction()
            }).catch(() => {
                WebApp.HapticFeedback.notificationOccurred("error");
                showSnackbar("❌", "answer_cancel_error")
                endAction()
            })
        }
        if (WebApp.platform === "unknown") {
            const confirmDelete = window.confirm("Вы уверены, что хотите отменить этот ответ?");
            if (confirmDelete) {
                doCancel();
            }
            return;
        }

        const wrapForDialog = (id: string) => {
            if (id === "cancel") {
                doCancel();
            }
        }
        showDialog("withdraw_answer_confirmation", {id: "cancel", type: "destructive", text:t("withdraw_answer_action")}, wrapForDialog, t("cancel_title"))
    }

    const selectAnswer = (e: React.MouseEvent<HTMLElement>) => {
        const questionIdString = e.currentTarget.dataset.questionid;
        if (!questionIdString) {
            console.error("answer not found", e.currentTarget);
            return;
        }
        const questionId = parseInt(questionIdString);
        let foundAnswer: Answer | undefined
        profile?.answers.find((answer) => {
            if (answer.question_id === questionId) {
                foundAnswer = answer;
                return true;
            }
            return false;
        })
        if (!foundAnswer) {
            setCurrentAnswerQuestionId(0);
            return;
        }
        setCurrentActionQuestion(foundAnswer);
        WebApp.HapticFeedback.impactOccurred("soft");
        setAnswerSettingsOpened(true);
    }

    const onProfilePhotoClick = () => {
        if (authProfile?.id === profileId) {
            setProfilePhotoModalOpened(true);
        }
    }

    const newProfilePhotoChange = (e: React.FormEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        if (!target.files) {
            return;
        }
        const file = target.files[0];
        if (!file) {
            return;
        }
        let shortName = file.name;
        if (shortName.length > 13) {
            shortName = shortName.substring(0, 5) + "..." + shortName.substring(shortName.length - 6);
        }
        setNewProfilePhotoName(shortName);
        setNewProfilePhotoSrc(URL.createObjectURL(file));
    }

    const saveProfilePhotoAction = () => {
        const callback = () => {
            setNewProfilePhotoLoading(true);
            const fileInput = document.getElementById("avatarInput") as HTMLInputElement;
            const file = fileInput.files?.[0];
            if (!file) {
                setNewProfilePhotoLoading(false);
                return;
            }

            saveProfilePhoto(file).then(() => {
                setNewProfilePhotoSrc("");
                setNewProfilePhotoName(t("select_file"));
                setNewProfilePhotoLoading(false);
                endAction();
                fetchProfile(profileId);
                showSnackbar("✅", t("profile_photo_updated"));
            }).catch(() => {
                setNewProfilePhotoLoading(false);
                showSnackbar("❌", t("profile_photo_update_error"));
            })
        }
        mustActionAllowed(callback);
    }

    const swipeHandlersForTabs = useSwipeable({
        onSwipedRight: (eventData) => setActiveTab("answers"),
        onSwipedLeft: (eventData) => setActiveTab("questions"),
        delta: 10,
        preventScrollOnSwipe: true,
        swipeDuration: 250,
    });

    if (error) {
        let title = t("profile_loading_error");
        let description = t("should_try_reload");
        return (
            <AppRoot>
                {history.length > 1 && <BackButton onClick={goBack} />}
                <Placeholder
                    header={title}
                    description={description}
                    action={<Button onClick={() => fetchProfile(profileId)}>{t("repeat")}</Button>}
                />
            </AppRoot>
        )
    }

    if (authProfileFetching || profileFetching) {
        return (
            <AppRoot>
                {history.length > 1 && <BackButton onClick={goBack} />}

                <Placeholder
                    description={<Spinner size="l" />}
                />
            </AppRoot>
        )
    }

    return (
        <AppRoot>
            {history.length > 1 && <BackButton onClick={goBack} />}

            <div className={"flex flex-col content-center justify-center items-center mt-5"}>
                <Avatar
                    size={96}
                    acronym={getAcronym(profile?.user)}
                    src={profile?.user.photo_url ?? ""}
                    onClick={onProfilePhotoClick}
                />
                <Title
                    className={"pt-5"}
                    level="3"
                    weight="2"
                >
                    {profile?.user.first_name + " " + profile?.user.last_name}
                </Title>
                <Text weight="3" className={"color-hint"}>
                    @{profile?.user.username ?? authProfile?.id}
                </Text>
            </div>
            <Section header={authProfile?.id === profileId ? t("ask_me_anything_owner") : t("ask_me_anything")}
                     footer={authProfile?.id === profileId ? t("ask_me_anything_footer_owner") : t("ask_me_anything_footer", {first_name: profile?.user.first_name})}
                     className={"w-full"}>
                <Textarea id={"questionField"}
                          maxLength={500}
                          disabled={sendQuestionLoading}
                          placeholder={t("question_placeholder")}/>
                <Cell className={"no-hover color-hint"} Component={"label"} after={<Switch id={"switchAnonymously"} defaultChecked={true}/>}>
                    <Text className={"w-full no-hover color-hint"}>{t("send_anonymous")}</Text>
                </Cell>
                <Button
                    mode="filled"
                    size="l"
                    stretched={true}
                    className={""}
                    onClick={sendQuestion}
                    disabled={sendQuestionLoading}
                    loading={sendQuestionLoading}
                >
                    {t("send")}
                </Button>
            </Section>

            <TabsList className={"mt-3"} id={"tabsList"}>
                <TabsItem
                    onClick={()=>{onActiveTabChange("answers")}}
                    selected={activeTab === "answers"}
                >
                    {t("answers")}
                </TabsItem>
                <TabsItem
                    onClick={()=>{onActiveTabChange("questions")}}
                    selected={activeTab === "questions"}
                >
                    {t("questions")}
                    {(authProfile?.id === profileId) && <Badge
                        mode={profile?.questions.length ? "primary" : "gray"}
                        type="number"
                    >
                        {profile?.questions.length}
                    </Badge>}
                </TabsItem>
            </TabsList>
            {activeTab === "answers" && <div {...swipeHandlersForTabs}>
                {!profile?.answers.length && <Placeholder
                    header={t("no_answers_yet")}
                    description={authProfile?.id === profileId ? t("no_answers_yet_description_owner") : t("no_answers_yet_description", {first_name: profile?.user.first_name})}
                    action={authProfile?.id === profileId ? <Button mode={"bezeled"} onClick={authProfile?.id === profileId ? shareProfile : focusQuestionField}>
                        {authProfile?.id === profileId ? t("send_link_action") : t("ask_question_action")}
                    </Button> : null}
                />}
                {profile?.answers.map((answer:Answer) => (
                    <Section key={"answer" + answer.question_id} id={"answer" + answer.question_id} className={"mt-4"} data-questionid={answer.question_id} onClick={selectAnswer}>
                        <Cell
                            after={<Avatar size={48} acronym={getAcronym(answer.from_user)} src={answer.from_user ? answer.from_user.photo_url ? answer.from_user.photo_url : "" : "/anon.webp"} />}
                            subhead={answer.from_user?.first_name ? answer.from_user?.first_name + " " + answer.from_user?.last_name : t("anonymous")}
                            subtitle={formatDateString(answer.created_at)}
                            className={"no-hover"}
                            multiline={true}
                        >
                            {answer.question}
                        </Cell>
                        <Cell
                            before={<Avatar size={48} acronym={getAcronym(profile?.user)} src={profile?.user.photo_url ?? ""} />}
                            subhead={profile?.user.first_name + " " + profile?.user.last_name}
                            subtitle={formatDateString(answer.updated_at)}
                            className={"no-hover"}
                            multiline={true}
                        >
                            {answer.answer}
                        </Cell>
                    </Section>
                ))}
            </div>}

            {activeTab === "questions" && <div {...swipeHandlersForTabs}>
                {!profile?.questions.length && <>
                    <Placeholder
                        header={t("no_questions_yet")}
                        description={authProfile?.id === profileId ? t("no_questions_yet_description_owner") : t("no_questions_yet_description")}
                        action={<Button mode={"bezeled"} onClick={authProfile?.id === profileId ? shareProfile : focusQuestionField}>
                            {authProfile?.id === profileId ? t("send_link_action") : t("ask_question_action")}
                        </Button>}
                    />
                </>}
                {(authProfile?.id !== profileId) && <Placeholder
                    description={t("questions_from_you")}
                />}
                {profile?.questions.map((question) => (
                    <Section key={"question" + question.question_id} id={"question" + question.question_id} className={"mt-4"}>
                        <Cell
                            after={<Avatar size={48} acronym={getAcronym(question.from_user)} src={question.from_user ? question.from_user.photo_url ? question.from_user.photo_url : "" : "/anon.webp"}/>}
                            subhead={question.from_user?.first_name ? question.from_user?.first_name + " " + question.from_user?.last_name : t("anonymous")}
                            subtitle={formatDateString(question.created_at)}
                            className={"no-hover"}
                            multiline={true}
                            data-profileid={question.from_user ? question.from_user.id : 0}
                            onClick={question.from_user ? goToProfileByEvent : undefined}
                        >
                            {question.question}
                        </Cell>
                        {currentAnswerQuestionId !== question.question_id && <div className={"flex justify-around"}>
                            {authProfile?.id === profileId && <Button mode={"plain"} stretched={true} data-questionid={question.question_id} onClick={startAnswering}>
                                {t("answer")}
                            </Button>}
                            <Divider/>
                            <Button className={"text-destructive"} loading={deleteLoading} mode={"plain"} stretched={true} data-questionid={question.question_id} onClick={deleteQuestion}>
                                {t("delete")}
                            </Button>
                        </div>}
                        {currentAnswerQuestionId === question.question_id && <Section>
                            <Textarea id={"answerField"} data-questionid={currentAnswerQuestionId} autoFocus onFocus={scrollToAnswerButton} placeholder="Enter your answer here..."/>
                            <div className={"flex justify-around gap-2 mr-3 ml-3 mt-1 mb-1"}>
                                <Button loading={sendAnswerLoading} id={"answerButton"} mode="filled" size="m" stretched={true}
                                        onClick={sendAnswer}>
                                    {t("send")}
                                </Button>
                                <Button mode="plain" size="m" stretched={true}
                                        onClick={endAction}>
                                    {t("cancel")}
                                </Button>
                            </div>
                        </Section>}
                    </Section>
                ))}

                {(authProfile?.id === profileId) && <div className={"mt-4 no-hover"}>
                    <Accordion
                        expanded={askedQuestionsOpened}
                        onChange={setAskedQuestionsOpened}
                    >
                        <AccordionSummary className={"no-hover"}>
                            {t("asked_questions")}
                        </AccordionSummary>
                        <AccordionContent className={"bg-secondary"}>
                            {profile?.asked_questions.map((answer:Answer) => (
                                <Section key={"asked" + answer.question_id} id={"asked" + answer.question_id} className={"mt-4"}
                                         data-profileid={answer.to_user.id}
                                         data-totab={answer.answer ? "answers" : "questions"}
                                         onClick={goToProfileByEvent}
                                >
                                    <Cell
                                        after={<Avatar size={48} acronym={getAcronym(answer.from_user)} src={answer.from_user ? answer.from_user.photo_url ? answer.from_user.photo_url : "" : "/anon.webp"} />}
                                        subhead={answer.from_user?.first_name ? answer.from_user?.first_name + " " + answer.from_user?.last_name : t("anonymous")}
                                        subtitle={formatDateString(answer.created_at)}
                                        className={"no-hover"}
                                        multiline={true}
                                    >
                                        {answer.question}
                                    </Cell>
                                    <Cell
                                        before={<Avatar size={48} acronym={getAcronym(profile?.user)} src={answer.to_user.photo_url ?? ""} />}
                                        subhead={answer.answer ? answer.to_user.first_name + " " + answer.to_user.last_name : undefined}
                                        subtitle={answer.answer ? formatDateString(answer.updated_at) : undefined}
                                        className={"no-hover"}
                                        multiline={true}
                                    >
                                        <Text className={(answer.answer ? "" : "italic color-hint")}>
                                            {answer.answer ? answer.answer : t("still_not_answered", {first_name: answer.to_user.first_name})}
                                        </Text>
                                    </Cell>
                                </Section>
                            ))}
                        </AccordionContent>
                    </Accordion>
                </div>
                }
            </div>}

            <div className={bottomMargin + (authProfile?.id === profileId ? " invisible" : "")}>
                <Button stretched={true} mode={"plain"} onClick={goToMyProfile}>
                    {t("go_to_my_profile")}
                </Button>
            </div>

            <Modal
                header={<ModalHeader></ModalHeader>}
                open={answerSettingsOpened}
                onOpenChange={stateAnswerAction}
                className={"pb-5"}

            >
                <div className={"flex justify-center text-center pl-5 pr-5 pt-2"}>
                    <Button after={"➜"} mode={"filled"}
                            loading={shareAnswerInProgress}
                            onClick={shareAnswer}
                            data-questionid={currentActionQuestion?.question_id}>
                        {t("share_answer")}
                    </Button>
                </div>

                {currentActionQuestion?.from_user &&
                    <Cell after={<Icon16Chevron/>} onClick={goToCurrentQuestionProfile}>
                        {t("open_profile")} {currentActionQuestion.from_user.first_name}
                    </Cell>}
                {authProfile?.id === profileId && <Cell after={cancelLoading ? <Spinner size="s"/> : <Icon16Chevron/>}
                                                        data-questionid={currentActionQuestion?.question_id}
                                                        onClick={cancelAnswer}>
                    {t("cancel_answer")}
                </Cell>}
                {authProfile?.id === profileId &&
                    <Cell data-questionid={currentActionQuestion?.question_id} className={"text-destructive"}
                          after={deleteLoading ? <Spinner size="s"/> : <Icon16Chevron/>} onClick={deleteQuestion}>
                        {t("delete_question")}
                    </Cell>}
            </Modal>

            <Modal
                header={<ModalHeader></ModalHeader>}
                open={profilePhotoModalOpened}
                onOpenChange={setProfilePhotoModalOpened}
                className={"pb-5"}
            >
                <Placeholder
                    description={<div className={"flex items-center justify-center gap-2"}>
                        <Avatar size={96} acronym={getAcronym(authProfile)} src={newProfilePhotoSrc !== "" ? newProfilePhotoSrc : authProfile?.photo_url ?? ""}/>
                        <CompressedImageInput id={"avatarInput"}
                            onChange={newProfilePhotoChange}
                            accept="image/*,capture=camera"
                            label={newProfilePhotoName}
                        />
                    </div>}
                    action={<Button
                        loading={newProfilePhotoLoading}
                        onClick={saveProfilePhotoAction}
                        disabled={!newProfilePhotoSrc}
                    >
                        {t("save_photo")}
                    </Button>}
                >
                    <Text>{t("update_profile_photo_title")}</Text>
                </Placeholder>
            </Modal>

            {snackbar}
        </AppRoot>
    );
};

export default App;
